import React, { useEffect, useRef, useState } from 'react';
import { getDomainConfig, CONTACT_INFO } from '../config';
import './ContactPage.css';

const TURNSTILE_SITE_KEYS = {
  '3fuel.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'equityfair.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'gr11d.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'kidzley.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'lyfter.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'onewerx.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'pmley.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'sprtze.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'syrvyr.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  '4emd.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'worke.ca': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'asheram.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'resultt.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  'snkre.com': '0x4AAAAAAD5HDEM6L_GJ_ZzS',
  '47ly.com': '0x4AAAAAAD5HKQfcWptdyxK8',
  'bundalini.com': '0x4AAAAAAD5HKQfcWptdyxK8',
  'drnzz.com': '0x4AAAAAAD5HKQfcWptdyxK8',
  'elyvt.com': '0x4AAAAAAD5HKQfcWptdyxK8',
  'evcle.com': '0x4AAAAAAD5HKQfcWptdyxK8',
  'fbryk.com': '0x4AAAAAAD5HKQfcWptdyxK8',
  'fish999.com': '0x4AAAAAAD5HKQfcWptdyxK8',
  'fnzly.com': '0x4AAAAAAD5HKQfcWptdyxK8',
  'food33.com': '0x4AAAAAAD5HKQfcWptdyxK8',
  'hhomez.com': '0x4AAAAAAD5HImPvhRXBhi_k',
  'ivoze.com': '0x4AAAAAAD5HImPvhRXBhi_k',
  'kmkle.com': '0x4AAAAAAD5HImPvhRXBhi_k',
  'kosmae.com': '0x4AAAAAAD5HImPvhRXBhi_k',
  'llaundromat.com': '0x4AAAAAAD5HImPvhRXBhi_k',
  'luxrry.com': '0x4AAAAAAD5HImPvhRXBhi_k',
  'metal33.com': '0x4AAAAAAD5HImPvhRXBhi_k',
  'mmineral.com': '0x4AAAAAAD5HImPvhRXBhi_k',
  'oil33.com': '0x4AAAAAAD5HImPvhRXBhi_k',
  'pllur.com': '0x4AAAAAAD5HJlepn0g29k-2',
  'qqarbon.com': '0x4AAAAAAD5HJlepn0g29k-2',
  'rb0t.com': '0x4AAAAAAD5HJlepn0g29k-2',
  'rprtz.com': '0x4AAAAAAD5HJlepn0g29k-2',
  'rrenew.com': '0x4AAAAAAD5HJlepn0g29k-2',
  'rrupa.com': '0x4AAAAAAD5HJlepn0g29k-2',
  'solrze.com': '0x4AAAAAAD5HJlepn0g29k-2',
  'trlze.com': '0x4AAAAAAD5HJlepn0g29k-2',
  'xp0e.com': '0x4AAAAAAD5HJlepn0g29k-2',
  'ynglee.com': '0x4AAAAAAD5HJlepn0g29k-2'
};

function ContactPage() {
  const config = getDomainConfig();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  const hostname = window.location.hostname.replace('www.', '');
  const captchaSiteKey = TURNSTILE_SITE_KEYS[hostname];
  const captchaRequired = Boolean(captchaSiteKey);
  const displayEmail = `info@${hostname}`;

  useEffect(() => {
    if (!captchaRequired) return undefined;

    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current || turnstileWidgetId.current !== null) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: captchaSiteKey,
        theme: 'light',
        callback: token => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken('')
      });
    };

    const existing = document.querySelector('script[data-storefront-turnstile]');
    if (existing) {
      if (window.turnstile) renderWidget();
      else existing.addEventListener('load', renderWidget, { once: true });
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.storefrontTurnstile = 'true';
      script.addEventListener('load', renderWidget, { once: true });
      document.head.appendChild(script);
    }

    return () => {
      if (window.turnstile && turnstileWidgetId.current !== null) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [captchaRequired, captchaSiteKey]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captchaRequired && !turnstileToken) {
      setError('Please complete the security check.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          domain: hostname,
          siteName: config.name,
          turnstileToken
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', company: '', message: '' });
        setTurnstileToken('');
      } else {
        setError('Failed to send message. Please try again.');
        if (window.turnstile && turnstileWidgetId.current !== null) {
          window.turnstile.reset(turnstileWidgetId.current);
          setTurnstileToken('');
        }
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Contact form error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <h1>Contact {config.name}</h1>
        <p>Get in touch with our team - we're here to help</p>
      </div>

      <div className="contact-content">
        <div className="contact-info">
          <h2>Contact Information</h2>

          <div className="info-section">
            <h3>Get In Touch</h3>
            <p>Email: {displayEmail}</p>
            <p>Phone: {CONTACT_INFO.phone}</p>
          </div>

          <div className="info-section">
            <h3>Business Hours</h3>
            <p>Monday - Friday: 8:00 AM - 6:00 PM EST</p>
            <p>Saturday: 9:00 AM - 2:00 PM EST</p>
            <p>Sunday: Closed</p>
          </div>

          <div className="info-section">
            <h3>Mailing Address</h3>
            <p>{CONTACT_INFO.address}</p>
            <p>{CONTACT_INFO.city}, {CONTACT_INFO.state} {CONTACT_INFO.zip}</p>
            <p>{CONTACT_INFO.country}</p>
          </div>
        </div>

        <div className="contact-form-container">
          <h2>Send Us a Message</h2>
          {error && (
            <div className="error-message" style={{ color: '#dc2626', padding: '1rem', marginBottom: '1rem', backgroundColor: '#fee2e2', borderRadius: '4px' }}>
              {error}
            </div>
          )}
          {submitted ? (
            <div className="success-message">
              <h3>Thank you for contacting us!</h3>
              <p>We'll get back to you within 24 hours.</p>
              <button onClick={() => setSubmitted(false)}>Send Another Message</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Your Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="company">Company Name</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </div>

              {captchaRequired && (
                <div className="form-group" aria-label="Security verification">
                  <div ref={turnstileRef}></div>
                </div>
              )}

              <button type="submit" className="submit-button" disabled={submitting || (captchaRequired && !turnstileToken)}>
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContactPage;
